import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import { useAppI18n } from "../hooks/useAppI18n";
import { getOrCreateAnonymousId } from "../utils/anonymousId";
import { getBackendUrl } from "../utils/backendUrl";

import { VoiceInputButton } from "./VoiceInputButton";
import { AttachImageThumbnails } from "./AttachImage";
import {
  AttachFileThumbnails,
  type AttachedFile,
} from "./AttachFile";
import { AttachButton } from "./AttachButton";
import { MessageAttachments, type AttachmentFileMeta } from "./MessageAttachments";
import { UploadToast } from "./UploadToast";
import { useFileSizeToast } from "../hooks/useFileSizeToast";

import "./ConversationContent.scss";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ConvMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  imageUrls?: string[];
  attachmentFiles?: AttachmentFileMeta[];
}

interface QuotaExceededInfo {
  isAnonymous: boolean;
  used: number;
  limit: number;
}

export interface ConversationContextForDraw {
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  topic: string;
}

interface ConversationContentProps {
  topic: string;
  /** Initial images from EntryScreen (sent with the first message) */
  initialImageUrls?: string[];
  /** Initial files from EntryScreen (sent with the first message) */
  initialFiles?: AttachedFile[];
  userId?: string | null;
  onBack: () => void;
  /** Pass conversation context (messages + topic); backend runs Summary then Generation. */
  onGoToDraw: (context: ConversationContextForDraw) => void;
  onQuotaExceeded?: (info: QuotaExceededInfo) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AI_BACKEND_URL = getBackendUrl();

const genId = () =>
  `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ─── Component ───────────────────────────────────────────────────────────────

export const ConversationContent: React.FC<ConversationContentProps> = ({
  topic,
  initialImageUrls,
  initialFiles,
  userId,
  onBack: _onBack,
  onGoToDraw,
  onQuotaExceeded,
}) => {
  const { t } = useAppI18n();
  const { toastMessage, showFileTooLarge, dismissToast } = useFileSizeToast();
  const anonymousId = useMemo(
    () => (userId ? "" : getOrCreateAnonymousId()),
    [userId],
  );

  const [messages, setMessages] = useState<ConvMessage[]>(() => {
    try {
      const saved = window.sessionStorage.getItem("ideaweave:convMessages");
      return saved ? (JSON.parse(saved) as ConvMessage[]) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [pendingImageUrls, setPendingImageUrls] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<AttachedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [readyToDraw, setReadyToDraw] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const initRef = useRef(false);
  const skipEnterRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const userScrolledUpRef = useRef(false);

  // Sync messages to sessionStorage so refresh restores them.
  // Strip large imageUrls (data URLs) to stay within sessionStorage limits;
  // keep only a count so the UI can show "[image ×N]" after restore.
  useEffect(() => {
    try {
      const lite = messages.map((m) => {
        if (!m.imageUrls?.length) {
          return m;
        }
        const { imageUrls: _strip, ...rest } = m;
        return { ...rest, _imageCount: _strip.length };
      });
      window.sessionStorage.setItem(
        "ideaweave:convMessages",
        JSON.stringify(lite),
      );
    } catch {
      // sessionStorage unavailable or quota exceeded
    }
  }, [messages]);

  const hasUserMsgs = messages.some((m) => m.role === "user");

  // Auto-scroll control: only user-initiated input (wheel/touch) toggles the flag.
  // We never use the generic "scroll" event to re-enable auto-scroll, because
  // programmatic scrollIntoView animations also fire scroll events and would
  // fight the user's intent.
  useEffect(() => {
    const container = messagesRef.current;
    if (!container) {
      return;
    }

    const isAtBottom = () =>
      container.scrollHeight - container.scrollTop - container.clientHeight <
      60;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        userScrolledUpRef.current = true;
      } else if (e.deltaY > 0 && userScrolledUpRef.current) {
        requestAnimationFrame(() => {
          if (isAtBottom()) {
            userScrolledUpRef.current = false;
          }
        });
      }
    };

    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchMove = (e: TouchEvent) => {
      const delta = e.touches[0].clientY - touchStartY;
      if (delta > 0) {
        userScrolledUpRef.current = true;
      } else if (delta < -10 && userScrolledUpRef.current) {
        requestAnimationFrame(() => {
          if (isAtBottom()) {
            userScrolledUpRef.current = false;
          }
        });
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: true });
    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handleTouchMove, { passive: true });
    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  useEffect(() => {
    const container = messagesRef.current;
    if (!container) {
      return;
    }
    container.style.paddingBottom = "";
    if (!userScrolledUpRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }
  }, [input]);

  const sendMessage = useCallback(
    async (text: string, imageUrls?: string[], files?: AttachedFile[]) => {
      const hasText = Boolean(text.trim());
      const hasImages = Boolean(imageUrls?.length);
      const hasFiles = Boolean(files?.length);
      if ((!hasText && !hasImages && !hasFiles) || loading) {
        return;
      }

      userScrolledUpRef.current = false;

      const displayContent =
        text.trim() ||
        (imageUrls?.length
          ? `[${t("conversation.attachedImages") ?? "Attached"} ${
              imageUrls.length
            }]`
          : "") ||
        (files?.length
          ? `[${t("conversation.attachedFiles") ?? "Attached files"} ${
              files.length
            }]`
          : "");
      const userMsg: ConvMessage = {
        id: genId(),
        role: "user",
        content: displayContent,
        imageUrls: imageUrls?.length ? [...imageUrls] : undefined,
        attachmentFiles: files?.length
          ? files.map((f) => ({ filename: f.filename, mimeType: f.mimeType }))
          : undefined,
      };

      const updatedMessages = [...messages, userMsg];
      setMessages(updatedMessages);
      setInput("");
      setPendingImageUrls([]);
      setPendingFiles([]);
      setLoading(true);
      setSearching(false);

      const abortController = new AbortController();
      abortRef.current = abortController;

      const aiMsgId = genId();
      let aiContent = "";

      try {
        const response = await fetch(
          `${AI_BACKEND_URL}/v1/ai/conversation/chat`,
          {
            method: "POST",
            headers: {
              Accept: "text/event-stream",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              messages: (() => {
                const prev = updatedMessages
                  .slice(0, -1)
                  .map((m) => ({ role: m.role, content: m.content }));
                const last = updatedMessages[updatedMessages.length - 1];
                const hasParts = imageUrls?.length || files?.length;
                const userContent = hasParts
                  ? [
                      ...(text.trim()
                        ? [{ type: "input_text", text: text.trim() }]
                        : []),
                      ...(imageUrls?.map((url: string) => ({
                        type: "input_image",
                        image_url: url,
                      })) ?? []),
                      ...(files?.map((f: AttachedFile) => ({
                        type: "input_file",
                        contentBase64: f.contentBase64,
                        mimeType: f.mimeType,
                        filename: f.filename,
                      })) ?? []),
                    ]
                  : last.content;
                return [...prev, { role: "user", content: userContent }];
              })(),
              topic,
              ...(userId ? { userId } : anonymousId ? { anonymousId } : {}),
            }),
            signal: abortController.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body");
        }

        const decoder = new TextDecoder();
        let buffer = "";
        let aiMsgInserted = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith("data: ")) {
              continue;
            }
            const data = trimmed.slice(6);
            if (data === "[DONE]") {
              break;
            }

            try {
              const event = JSON.parse(data);
              if (event.type === "quota_exceeded") {
                setLoading(false);
                if (onQuotaExceeded) {
                  onQuotaExceeded({
                    isAnonymous: event.isAnonymous ?? !userId,
                    used: event.used ?? 0,
                    limit: event.limit ?? 0,
                  });
                }
                return;
              } else if (event.type === "search_started") {
                setSearching(true);
              } else if (event.type === "delta" && event.content) {
                aiContent += event.content;
                if (!aiMsgInserted) {
                  aiMsgInserted = true;
                  setLoading(false);
                  setSearching(false);
                  const contentSnapshot = aiContent;
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: aiMsgId,
                      role: "assistant",
                      content: contentSnapshot,
                    },
                  ]);
                } else {
                  const contentSnapshot = aiContent;
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === aiMsgId ? { ...m, content: contentSnapshot } : m,
                    ),
                  );
                }
              } else if (event.type === "done") {
                setSearching(false);
                if (event.ready_to_draw) {
                  setReadyToDraw(true);
                }
                if (aiMsgInserted) {
                  const cleaned = (event.content || aiContent)
                    .replace(/\[DRAW_READY\]/g, "")
                    .trimEnd();
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === aiMsgId ? { ...m, content: cleaned } : m,
                    ),
                  );
                }
              } else if (event.type === "error") {
                throw new Error(event.message || "Stream error");
              }
            } catch (parseErr) {
              // ignore non-JSON SSE lines
            }
          }
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          return;
        }
        console.error("[Conversation] Stream error:", err);
        if (!aiContent) {
          setMessages((prev) => [
            ...prev.filter((m) => m.id !== aiMsgId),
            {
              id: aiMsgId,
              role: "assistant",
              content: "Sorry, something went wrong. Please try again.",
            },
          ]);
        }
      } finally {
        setLoading(false);
        setSearching(false);
        abortRef.current = null;
      }
    },
    [loading, messages, topic, userId, anonymousId, onQuotaExceeded, t],
  );

  useEffect(() => {
    if (topic && !initRef.current) {
      initRef.current = true;
      // If messages were restored from sessionStorage (page refresh), skip re-sending
      if (messages.length === 0) {
        sendMessage(
          topic,
          initialImageUrls?.length ? initialImageUrls : undefined,
          initialFiles?.length ? initialFiles : undefined,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, sendMessage]);

  const handleSend = () => {
    if (
      input.trim() ||
      pendingImageUrls.length > 0 ||
      pendingFiles.length > 0
    ) {
      sendMessage(
        input,
        pendingImageUrls.length > 0 ? pendingImageUrls : undefined,
        pendingFiles.length > 0 ? pendingFiles : undefined,
      );
    }
  };

  const removePendingImage = useCallback((index: number) => {
    setPendingImageUrls((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const removePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (e.nativeEvent.isComposing || skipEnterRef.current) {
        skipEnterRef.current = false;
        return;
      }
      e.preventDefault();
      handleSend();
    }
  };

  const handleCompositionEnd = () => {
    skipEnterRef.current = true;
    setTimeout(() => {
      skipEnterRef.current = false;
    }, 100);
  };

  const handleVoiceTranscript = useCallback((text: string) => {
    setInput((prev) => (prev ? `${prev} ${text}` : text));
    textareaRef.current?.focus();
  }, []);

  const handleGoToDraw = useCallback(() => {
    // Fire-and-forget: update user persona from this conversation (logged-in only)
    if (userId && messages.length > 0) {
      const body = {
        userId,
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
        topic,
      };
      fetch(`${AI_BACKEND_URL}/v1/ai/persona/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).catch(() => {});
    }
    onGoToDraw({
      messages: messages.map((m) => ({ role: m.role, content: m.content })),
      topic,
    });
  }, [onGoToDraw, userId, messages, topic]);

  const renderInline = (text: string, keyPrefix: string) => {
    const inlinePattern = /(\*\*[^*]+\*\*|\(?\[[^\]]+\]\([^)]+\)\)?)/g;
    const parts = text.split(inlinePattern);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>;
      }
      const linkMatch = part.match(/^\(?\[([^\]]+)\]\(([^)]+)\)\)?$/);
      if (linkMatch) {
        return (
          <a
            key={`${keyPrefix}-a${i}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="iw-conversation__link"
          >
            {linkMatch[1]}
          </a>
        );
      }
      return <React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>;
    });
  };

  const renderText = (content: string) => {
    const lines = content
      .replace(/\[DRAW_READY\]/g, "")
      .trimEnd()
      .split("\n");
    const elements: React.ReactNode[] = [];
    let listItems: { text: string; ordered: boolean; num?: string }[] = [];
    let blockKey = 0;

    const flushList = () => {
      if (listItems.length === 0) {
        return;
      }
      const isOrdered = listItems[0].ordered;
      const Tag = isOrdered ? "ol" : "ul";
      elements.push(
        <Tag key={`list-${blockKey++}`} className="iw-conversation__list">
          {listItems.map((item, i) => (
            <li key={i}>{renderInline(item.text, `li-${blockKey}-${i}`)}</li>
          ))}
        </Tag>,
      );
      listItems = [];
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      if (!trimmed) {
        flushList();
        continue;
      }

      // Headers: ## or ###
      const headerMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
      if (headerMatch) {
        flushList();
        const level = headerMatch[1].length;
        const cls = `iw-conversation__h${Math.min(level, 4)}`;
        elements.push(
          <div key={`h-${blockKey++}`} className={cls}>
            {renderInline(headerMatch[2], `h-${blockKey}`)}
          </div>,
        );
        continue;
      }

      // Horizontal rule: --- or ***
      if (/^(-{3,}|\*{3,})$/.test(trimmed)) {
        flushList();
        elements.push(
          <hr key={`hr-${blockKey++}`} className="iw-conversation__hr" />,
        );
        continue;
      }

      // Blockquote: > text
      if (trimmed.startsWith("> ")) {
        flushList();
        elements.push(
          <blockquote
            key={`bq-${blockKey++}`}
            className="iw-conversation__blockquote"
          >
            {renderInline(trimmed.slice(2), `bq-${blockKey}`)}
          </blockquote>,
        );
        continue;
      }

      // Unordered list: - item, * item, • item
      const ulMatch = trimmed.match(/^[-*•]\s+(.+)$/);
      if (ulMatch) {
        if (listItems.length > 0 && listItems[0].ordered) {
          flushList();
        }
        listItems.push({ text: ulMatch[1], ordered: false });
        continue;
      }

      // Ordered list: 1. item, 2. item
      const olMatch = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
      if (olMatch) {
        if (listItems.length > 0 && !listItems[0].ordered) {
          flushList();
        }
        listItems.push({ text: olMatch[2], ordered: true, num: olMatch[1] });
        continue;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p key={`p-${blockKey++}`} className="iw-conversation__para">
          {renderInline(trimmed, `p-${blockKey}`)}
        </p>,
      );
    }

    flushList();
    return elements;
  };

  return (
    <div className="iw-conversation">
      {/* Messages */}
      <div ref={messagesRef} className="iw-conversation__messages">
        {messages.map((msg) => (
          <div
            key={msg.id}
            data-msg-id={msg.id}
            className={`iw-conversation__msg iw-conversation__msg--${msg.role}`}
          >
            {msg.role === "assistant" && (
              <div className="iw-conversation__ai-avatar">
                <span className="material-symbols-outlined">lightbulb</span>
              </div>
            )}
            <div className="iw-conversation__bubble">
              {msg.role === "assistant" && (
                <div className="iw-conversation__label">IdeaWeave AI</div>
              )}
              {msg.role === "user" && (
                <MessageAttachments
                  imageUrls={msg.imageUrls}
                  files={msg.attachmentFiles}
                />
              )}
              <div className="iw-conversation__text">
                {renderText(msg.content)}
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="iw-conversation__msg iw-conversation__msg--assistant">
            <div className="iw-conversation__ai-avatar">
              <span className="material-symbols-outlined">lightbulb</span>
            </div>
            <div className="iw-conversation__bubble">
              <div className="iw-conversation__label">IdeaWeave AI</div>
              {searching ? (
                <div className="iw-conversation__searching">
                  <span className="material-symbols-outlined">
                    travel_explore
                  </span>
                  <span>
                    {t("conversation.searching") || "Searching the web..."}
                  </span>
                </div>
              ) : (
                <div className="iw-conversation__typing">
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Bottom: Input + Go to Draw */}
      <div className="iw-conversation__bottom">
        <div className="iw-conversation__input-wrap">
          <AttachImageThumbnails
            urls={pendingImageUrls}
            onRemove={removePendingImage}
            className="iw-conversation__thumbnails"
          />
          <AttachFileThumbnails
            files={pendingFiles}
            onRemove={removePendingFile}
            className="iw-conversation__file-list"
          />
          <textarea
            ref={textareaRef}
            className="iw-conversation__textarea"
            placeholder={t("conversation.placeholder")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionEnd={handleCompositionEnd}
            disabled={loading}
            rows={1}
          />
          <div className="iw-conversation__input-actions">
            <VoiceInputButton
              onTranscript={handleVoiceTranscript}
              disabled={loading}
              variant="default"
              layout="default"
              className="iw-conversation__voice-btn"
            />
            <div className="iw-conversation__input-actions-right">
              <AttachButton
                onAddImages={(urls) => {
                  setPendingImageUrls((prev) => [...prev, ...urls]);
                  textareaRef.current?.focus();
                }}
                onAddFiles={(files) => {
                  setPendingFiles((prev) => [...prev, ...files]);
                  textareaRef.current?.focus();
                }}
                onFileTooLarge={showFileTooLarge}
                disabled={loading}
                buttonClassName="iw-conversation__image-btn"
                title={t("conversation.addAttachment") ?? "Add image / file"}
              />
              <button
                className="iw-conversation__send-btn"
                onClick={handleSend}
                disabled={
                  (!input.trim() &&
                    pendingImageUrls.length === 0 &&
                    pendingFiles.length === 0) ||
                  loading
                }
              >
                {t("conversation.send")}
                <span className="material-symbols-outlined">arrow_upward</span>
              </button>
            </div>
          </div>
        </div>

        {hasUserMsgs && !loading && (
          <button
            className={`iw-conversation__draw-btn${
              readyToDraw ? " iw-conversation__draw-btn--highlighted" : ""
            }`}
            onClick={handleGoToDraw}
          >
            <span className="material-symbols-outlined">draw</span>
            <span>{t("conversation.goToDraw")}</span>
          </button>
        )}
      </div>
      {toastMessage && (
        <UploadToast message={toastMessage} onClose={dismissToast} />
      )}
    </div>
  );
};
